"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { LevelView } from "@/lib/level";
import { smarticoLevelView } from "./level";
import { hasTimedMission, selectMission, type MissionView } from "./mission";
import { useSmartico } from "./SmarticoProvider";
import type { SmarticoLevel, SmarticoMission } from "./types";

/* Reading Smartico.

   Everything here is gated on `api`, which the provider only publishes once a
   player is identified. Signed out, or unconfigured, every hook returns null
   and the widgets fall back — none of this fires a request.

   The query keys all start with "smartico" so a sign-out can drop the previous
   player's data in one call. */

/* Smartico caches getMissions() for 30 seconds on its own side, so asking more
   often than that buys nothing. */
const MISSIONS_STALE_MS = 30_000;
/* Levels are operator configuration. They change when someone edits them. */
const LEVELS_STALE_MS = 5 * 60_000;
/* A mission's window can open or close with the page just sitting there, so
   eligibility is re-evaluated on a clock — but only when some mission in the
   feed actually has one. */
const ELIGIBILITY_TICK_MS = 15_000;

const missionsKey = ["smartico", "missions"] as const;
const levelsKey = ["smartico", "levels"] as const;

const useSmarticoMissions = () => {
  const { api } = useSmartico();
  const client = useQueryClient();

  return useQuery({
    queryKey: missionsKey,
    enabled: api !== null,
    staleTime: MISSIONS_STALE_MS,
    queryFn: async () => {
      if (api === null) return [] as SmarticoMission[];

      /* Smartico pushes updates for as long as the subscription lives, so
         progress moves without a refetch — the callback writes straight into
         the cache the selector already reads. */
      return api.getMissions({
        onUpdate: (missions) => client.setQueryData(missionsKey, missions),
      });
    },
  });
};

/* A clock that only runs when something is actually counting down. */
const useTick = (active: boolean, everyMs: number) => {
  const [now, setNow] = useState(0);

  useEffect(() => {
    if (!active) return;

    /* Set on mount rather than in the initialiser: the first render also
       happens on the server, and a timestamp baked in there would differ from
       the client's and break hydration. */
    setNow(Date.now());

    const timer = window.setInterval(() => setNow(Date.now()), everyMs);

    return () => window.clearInterval(timer);
  }, [active, everyMs]);

  return now;
};

/* The one mission the sidebar shows, or null when there is nothing to show —
   which is a real answer, not an error: a player can have finished everything
   on offer. */
export const useSmarticoMission = (): {
  mission: MissionView | null;
  isPending: boolean;
} => {
  const { api } = useSmartico();
  const query = useSmarticoMissions();

  const timed = hasTimedMission(query.data);
  const tick = useTick(timed, ELIGIBILITY_TICK_MS);

  /* Before the first tick lands, and for a feed with no clocks in it at all,
     the selection is evaluated against the current instant rather than a stale
     zero. */
  const now = tick === 0 ? Date.now() : tick;

  return {
    mission: api === null ? null : selectMission(query.data, now),
    isPending: api !== null && query.isPending,
  };
};

/* XP — `ach_points_ever` on the Smartico profile.

   Not a query: the value is already in the browser and Smartico pushes a
   `props_change` whenever it moves, so subscribing beats polling. `gf_closing`
   fires when the player shuts Smartico's dashboard, where they may have just
   claimed something the profile has not broadcast yet. */
const useSmarticoXp = () => {
  const { api } = useSmartico();
  const [xp, setXp] = useState(0);

  useEffect(() => {
    if (api === null || !window._smartico) {
      setXp(0);

      return;
    }

    const smartico = window._smartico;

    const read = () => {
      try {
        setXp(api.getUserProfile().ach_points_ever ?? 0);
      } catch {
        /* The profile is not readable between an identify and the first
           payload. Leaving the previous value alone is better than flashing a
           zero into a progress bar. */
      }
    };

    read();
    smartico.on("props_change", read);
    smartico.on("gf_closing", read);

    return () => {
      smartico.off("props_change", read);
      smartico.off("gf_closing", read);
    };
  }, [api]);

  return xp;
};

/* The player's level and how far they are into it. */
export const useSmarticoLevel = (): { level: LevelView | null; isPending: boolean } => {
  const { api } = useSmartico();
  const xp = useSmarticoXp();

  const query = useQuery({
    queryKey: levelsKey,
    enabled: api !== null,
    staleTime: LEVELS_STALE_MS,
    queryFn: async () => (api === null ? ([] as SmarticoLevel[]) : api.getLevels()),
  });

  return {
    level: api === null ? null : smarticoLevelView(query.data, xp),
    isPending: api !== null && query.isPending,
  };
};
